import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { Card } from '../components/ui/card';
import { X, TrendingUp, TrendingDown, Shield, Target, Users, AlertCircle, Cloud, Activity, Radio, Wifi } from 'lucide-react';
import LiveStreamModal from './LiveStreamModal';
import WhereToWatchSection from './WhereToWatchSection';
import AsianHandicapInlineCard from './AsianHandicapInlineCard';
import GoalTimingInlineCard from './GoalTimingInlineCard';
import H2HMatchupChip from './H2HMatchupChip';
import HardeningChip from './HardeningChip';

const API = process.env.REACT_APP_BACKEND_URL;

// Iter 134j — relative-time formatter for the "Last sync · 2 min ago" badge.
const _sinceLabel = (iso) => {
  if (!iso) return "moments ago";
  try {
    const diffMs = Date.now() - new Date(iso).getTime();
    const m = Math.max(0, Math.round(diffMs / 60000));
    if (m < 1) return "just now";
    if (m < 60) return `${m} min ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h} h ago`;
    return `${Math.round(h / 24)} d ago`;
  } catch { return "moments ago"; }
};

const MatchAnalysisModal = ({ analysis, onClose }) => {
  const [showStream, setShowStream] = useState(false);
  const [h2hRich, setH2hRich] = useState(null);
  const [hardening, setHardening] = useState(null);
  if (!analysis) return null;

  const { match_info, head_to_head, home_team_analysis, away_team_analysis,
          league_standings, attack_defense_matchup, key_factors,
          expert_commentary, prediction_breakdown,
          data_provenance } = analysis;

  // Iter 59 UI — lazy-fetch H2H + hardening payloads for the two chips.
  useEffect(() => {
    let cancelled = false;
    const fid = match_info?.fixture_id;
    const homeId = match_info?.home_team_id || match_info?.home_id;
    const awayId = match_info?.away_team_id || match_info?.away_id;
    const tok = (typeof window !== 'undefined')
      ? (localStorage.getItem('ndaku_token') || '')
      : '';
    const headers = tok ? { Authorization: `Bearer ${tok}` } : {};
    if (homeId && awayId) {
      axios.get(`${API}/api/gold/h2h/${homeId}/${awayId}`, { headers })
        .then(r => { if (!cancelled) setH2hRich(r.data); })
        .catch(() => {});
    }
    if (fid) {
      axios.get(`${API}/api/gold/hardening/${fid}`, { headers })
        .then(r => { if (!cancelled) setHardening(r.data); })
        .catch(() => {});
    }
    return () => { cancelled = true; };
  }, [match_info?.fixture_id, match_info?.home_team_id, match_info?.away_team_id, match_info?.home_id, match_info?.away_id]);

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto border border-blue-500/30 shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 p-6 rounded-t-2xl flex justify-between items-start z-10 gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="text-3xl font-bold text-white">{match_info.home_team} vs {match_info.away_team}</h2>
            <p className="text-blue-100 mt-1">{match_info.league} • {new Date(match_info.date).toLocaleDateString()}</p>
            <p className="text-blue-200 text-sm mt-1">📍 {match_info.venue}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* Iter 124 — Watch 3D Tracker button.
                Opens LiveStreamModal which renders the
                `widgets.thesports01.com/.../3d/football?profile=…&id=…`
                iframe pulled from aiscore via /api/streams/{fixture_id}. */}
            {match_info.fixture_id && (
              <button
                type="button"
                onClick={() => setShowStream(true)}
                data-testid="match-analysis-watch-3d-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold text-sm shadow-lg shadow-emerald-500/30 transition"
                title="Watch the live 3D animation tracker"
              >
                <Radio className="w-4 h-4" />
                <span className="hidden sm:inline">Watch 3D Tracker</span>
                <span className="sm:hidden">3D</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="bg-white/20 hover:bg-white/30 p-2 rounded-full transition"
            >
              <X className="w-6 h-6 text-white" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Iter 134j — Live-data provenance badge.  Surfaces which
              real upstream sources contributed to this analysis, plus
              a relative "Last sync" timestamp.  Built to make data
              freshness visible to users and to make any future
              fabrication regression spot-checkable. */}
          {data_provenance?.is_live_data && (
            <div
              className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/30"
              data-testid="match-data-provenance-badge"
            >
              <div className="flex items-center gap-2 text-emerald-300 text-xs sm:text-sm">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 animate-ping"></span>
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400"></span>
                </span>
                <Wifi className="w-3.5 h-3.5" />
                <span className="font-semibold uppercase tracking-[0.14em] text-[10px] sm:text-xs">
                  Powered by live API data
                </span>
                <span className="hidden sm:inline text-emerald-200/70">·</span>
                <span className="text-emerald-200/80 tabular-nums">
                  Last sync · {_sinceLabel(data_provenance.last_sync)}
                </span>
              </div>
              {data_provenance.sources?.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5">
                  {data_provenance.sources.map((src) => (
                    <span
                      key={src}
                      className="text-[9px] font-mono tracking-tight text-emerald-200/80 bg-emerald-500/15 border border-emerald-500/25 px-1.5 py-0.5 rounded"
                    >
                      {src}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Expert Commentary — Iter 61 journalist-style Markdown */}
          <Card className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 border-purple-500/30 p-6" data-testid="expert-analysis-card">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-6 h-6 text-purple-400" />
              <h3 className="text-2xl font-bold text-white">Expert Analysis</h3>
            </div>
            <div className="text-gray-200 leading-relaxed expert-analysis-markdown" data-testid="expert-analysis-body">
              <ReactMarkdown
                components={{
                  h2: ({ node, ...props }) => (
                    <h2 className="text-2xl font-bold text-white mt-1 mb-3 tracking-tight" {...props} />
                  ),
                  h3: ({ node, ...props }) => (
                    <h3 className="text-lg font-semibold text-purple-200 mt-5 mb-2 uppercase tracking-wide" {...props} />
                  ),
                  p: ({ node, ...props }) => (
                    <p className="text-gray-200 leading-7 mb-3" {...props} />
                  ),
                  strong: ({ node, ...props }) => (
                    <strong className="text-white font-semibold" {...props} />
                  ),
                  ul: ({ node, ...props }) => (
                    <ul className="list-disc pl-5 space-y-1 mb-3 text-gray-200" {...props} />
                  ),
                  li: ({ node, ...props }) => (
                    <li className="leading-6" {...props} />
                  ),
                }}
              >
                {expert_commentary || ''}
              </ReactMarkdown>
            </div>
          </Card>

          {/* Iter 136 — Where to Watch on TV (TheSportsDB) */}
          <WhereToWatchSection matchInfo={match_info} />

          {/* Prediction Breakdown */}
          <Card className="bg-gradient-to-br from-green-900/30 to-emerald-900/30 border-green-500/30 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Target className="w-6 h-6 text-green-400" />
              <h3 className="text-2xl font-bold text-white">Our Prediction</h3>
            </div>
            <div className="bg-green-500/20 rounded-lg p-4 mb-4">
              <div className="text-center">
                <p className="text-green-400 text-sm mb-1">Predicted Outcome</p>
                <p className="text-3xl font-bold text-white">{prediction_breakdown.main_prediction}</p>
                <p className="text-green-300 text-lg mt-1">{prediction_breakdown.confidence}% Confidence</p>
              </div>
            </div>
            <div className="text-gray-300 whitespace-pre-line">
              {prediction_breakdown.explanation}
            </div>
            <p className="text-gray-400 text-sm mt-3">Model: {prediction_breakdown.model}</p>
          </Card>

          {/* Iter 59 UI — H2H Matchup + Hardening chips right at the top
              of the analysis so users see the model's self-critique
              without needing to open the /gold dossier. */}
          {h2hRich && h2hRich.status !== 'no_data' && h2hRich.status !== 'engine_error' && (
            <H2HMatchupChip
              h2h={h2hRich}
              homeName={match_info?.home_team || 'Home'}
              awayName={match_info?.away_team || 'Away'}
            />
          )}
          {hardening && (
            <HardeningChip
              hardening={hardening.hardening}
              dataQuality={hardening.data_quality}
              veto={hardening.veto}
              lineup={hardening.lineup}
              tactical={hardening.tactical}
            />
          )}

          {/* Head to Head */}
          <Card className="bg-gradient-to-br from-orange-900/30 to-red-900/30 border-orange-500/30 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Activity className="w-6 h-6 text-orange-400" />
              <h3 className="text-2xl font-bold text-white">Head-to-Head Record</h3>
            </div>
            <p className="text-gray-300 mb-4">{head_to_head.summary}</p>
            
            {head_to_head.meetings > 0 && (
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="bg-blue-500/20 rounded-lg p-3 text-center">
                  <p className="text-blue-400 text-sm">Home Wins</p>
                  <p className="text-2xl font-bold text-white">{head_to_head.record.home_wins}</p>
                </div>
                <div className="bg-gray-500/20 rounded-lg p-3 text-center">
                  <p className="text-gray-400 text-sm">Draws</p>
                  <p className="text-2xl font-bold text-white">{head_to_head.record.draws}</p>
                </div>
                <div className="bg-red-500/20 rounded-lg p-3 text-center">
                  <p className="text-red-400 text-sm">Away Wins</p>
                  <p className="text-2xl font-bold text-white">{head_to_head.record.away_wins}</p>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <p className="text-gray-400 text-sm">Average Goals</p>
                <p className="text-white font-semibold">{head_to_head.avg_goals?.toFixed(1) || 'N/A'}</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">BTTS %</p>
                <p className="text-white font-semibold">{head_to_head.btts_percentage?.toFixed(0) || '0'}%</p>
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-orange-500/10 rounded-lg">
              <p className="text-orange-300 text-sm font-semibold">Trend: {head_to_head.trend}</p>
            </div>
          </Card>

          {/* Team Analysis - Side by Side */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Home Team */}
            <Card className="bg-gradient-to-br from-blue-900/30 to-cyan-900/30 border-blue-500/30 p-6" data-testid="home-team-analysis-card">
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-6 h-6 text-blue-400" />
                <h3 className="text-xl font-bold text-white">{home_team_analysis.team_name}</h3>
                <span className="text-blue-400 text-sm">({home_team_analysis.location})</span>
              </div>
              
              <div className="bg-blue-500/20 rounded-lg p-3 mb-4">
                <p className="text-blue-300 font-semibold">{home_team_analysis.form_assessment}</p>
                <p className="text-gray-300 text-sm">{home_team_analysis.record}</p>
                <p className="text-gray-400 text-sm">Win Rate: {home_team_analysis.win_percentage?.toFixed(0)}%</p>
                {home_team_analysis.using_season_fallback && (
                  <p className="text-amber-300 text-xs mt-1">Showing season averages — venue-specific sample too thin</p>
                )}
              </div>

              <div className="space-y-3">
                <div>
                  <p className="text-gray-400 text-sm mb-1">⚽ Attack</p>
                  <p className="text-white">
                    {home_team_analysis.attack.rating}
                    {home_team_analysis.attack.rating !== "Not enough data" && (
                      <> — {home_team_analysis.attack.scoring_consistency?.toFixed(0)}% scoring consistency</>
                    )}
                  </p>
                  {home_team_analysis.sample_size >= 5 && (
                    <p className="text-gray-400 text-sm">Avg: {home_team_analysis.goals[`${home_team_analysis.location.toLowerCase()}_avg_scored`]?.toFixed(1)} goals</p>
                  )}
                </div>
                
                <div>
                  <p className="text-gray-400 text-sm mb-1">🛡️ Defense</p>
                  <p className="text-white">
                    {home_team_analysis.defense.rating}
                    {home_team_analysis.defense.rating !== "Not enough data" && (
                      <> — {home_team_analysis.defense.clean_sheet_percentage?.toFixed(0)}% clean sheets</>
                    )}
                  </p>
                  {home_team_analysis.sample_size >= 5 && (
                    <p className="text-gray-400 text-sm">Concedes: {home_team_analysis.goals[`${home_team_analysis.location.toLowerCase()}_avg_conceded`]?.toFixed(1)} goals</p>
                  )}
                </div>

                <div className="border-t border-blue-500/30 pt-3 mt-3">
                  <p className="text-green-400 text-sm font-semibold mb-2">✅ Strengths:</p>
                  <ul className="text-gray-300 text-sm space-y-1">
                    {home_team_analysis.strengths.map((s, i) => (
                      <li key={i}>• {s}</li>
                    ))}
                  </ul>
                </div>

                {home_team_analysis.weaknesses[0] !== "No major weaknesses identified" &&
                 home_team_analysis.weaknesses[0] !== "Awaiting more matches to identify weaknesses" && (
                  <div>
                    <p className="text-orange-400 text-sm font-semibold mb-2">⚠️ Weaknesses:</p>
                    <ul className="text-gray-300 text-sm space-y-1">
                      {home_team_analysis.weaknesses.map((w, i) => (
                        <li key={i}>• {w}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </Card>

            {/* Away Team */}
            <Card className="bg-gradient-to-br from-red-900/30 to-pink-900/30 border-red-500/30 p-6" data-testid="away-team-analysis-card">
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-6 h-6 text-red-400" />
                <h3 className="text-xl font-bold text-white">{away_team_analysis.team_name}</h3>
                <span className="text-red-400 text-sm">({away_team_analysis.location})</span>
              </div>
              
              <div className="bg-red-500/20 rounded-lg p-3 mb-4">
                <p className="text-red-300 font-semibold">{away_team_analysis.form_assessment}</p>
                <p className="text-gray-300 text-sm">{away_team_analysis.record}</p>
                <p className="text-gray-400 text-sm">Win Rate: {away_team_analysis.win_percentage?.toFixed(0)}%</p>
                {away_team_analysis.using_season_fallback && (
                  <p className="text-amber-300 text-xs mt-1">Showing season averages — venue-specific sample too thin</p>
                )}
              </div>

              <div className="space-y-3">
                <div>
                  <p className="text-gray-400 text-sm mb-1">⚽ Attack</p>
                  <p className="text-white">
                    {away_team_analysis.attack.rating}
                    {away_team_analysis.attack.rating !== "Not enough data" && (
                      <> — {away_team_analysis.attack.scoring_consistency?.toFixed(0)}% scoring consistency</>
                    )}
                  </p>
                  {away_team_analysis.sample_size >= 5 && (
                    <p className="text-gray-400 text-sm">Avg: {away_team_analysis.goals[`${away_team_analysis.location.toLowerCase()}_avg_scored`]?.toFixed(1)} goals</p>
                  )}
                </div>
                
                <div>
                  <p className="text-gray-400 text-sm mb-1">🛡️ Defense</p>
                  <p className="text-white">
                    {away_team_analysis.defense.rating}
                    {away_team_analysis.defense.rating !== "Not enough data" && (
                      <> — {away_team_analysis.defense.clean_sheet_percentage?.toFixed(0)}% clean sheets</>
                    )}
                  </p>
                  {away_team_analysis.sample_size >= 5 && (
                    <p className="text-gray-400 text-sm">Concedes: {away_team_analysis.goals[`${away_team_analysis.location.toLowerCase()}_avg_conceded`]?.toFixed(1)} goals</p>
                  )}
                </div>

                <div className="border-t border-red-500/30 pt-3 mt-3">
                  <p className="text-green-400 text-sm font-semibold mb-2">✅ Strengths:</p>
                  <ul className="text-gray-300 text-sm space-y-1">
                    {away_team_analysis.strengths.map((s, i) => (
                      <li key={i}>• {s}</li>
                    ))}
                  </ul>
                </div>

                {away_team_analysis.weaknesses[0] !== "No major weaknesses identified" &&
                 away_team_analysis.weaknesses[0] !== "Awaiting more matches to identify weaknesses" && (
                  <div>
                    <p className="text-orange-400 text-sm font-semibold mb-2">⚠️ Weaknesses:</p>
                    <ul className="text-gray-300 text-sm space-y-1">
                      {away_team_analysis.weaknesses.map((w, i) => (
                        <li key={i}>• {w}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* League Standings */}
          <Card className="bg-gradient-to-br from-yellow-900/30 to-amber-900/30 border-yellow-500/30 p-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-6 h-6 text-yellow-400" />
              <h3 className="text-2xl font-bold text-white">League Position & Motivation</h3>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-blue-500/20 rounded-lg p-4">
                <p className="text-blue-400 text-sm mb-1">{match_info.home_team}</p>
                <p className="text-3xl font-bold text-white">#{league_standings.home_position}</p>
                <p className="text-gray-300 text-sm mt-2">{league_standings.home_context}</p>
                <p className="text-blue-300 text-sm mt-2 font-semibold">{league_standings.home_motivation}</p>
              </div>
              
              <div className="bg-red-500/20 rounded-lg p-4">
                <p className="text-red-400 text-sm mb-1">{match_info.away_team}</p>
                <p className="text-3xl font-bold text-white">#{league_standings.away_position}</p>
                <p className="text-gray-300 text-sm mt-2">{league_standings.away_context}</p>
                <p className="text-red-300 text-sm mt-2 font-semibold">{league_standings.away_motivation}</p>
              </div>
            </div>
            
            <div className="bg-yellow-500/10 rounded-lg p-3">
              <p className="text-yellow-300">
                <strong>Position Advantage:</strong> {league_standings.position_advantage} • 
                <strong> Motivation:</strong> {league_standings.motivation_edge}
              </p>
            </div>
          </Card>

          {/* Attack vs Defense Matchup */}
          <Card className="bg-gradient-to-br from-indigo-900/30 to-violet-900/30 border-indigo-500/30 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Target className="w-6 h-6 text-indigo-400" />
              <h3 className="text-2xl font-bold text-white">Attack vs Defense Matchup</h3>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-gray-400 text-sm mb-2">Home Attack vs Away Defense</p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-blue-500/20 rounded p-2 text-center">
                    <p className="text-blue-400 text-xs">Attack</p>
                    <p className="text-white font-bold">{attack_defense_matchup.home_attack_strength?.toFixed(2)}</p>
                  </div>
                  <span className="text-gray-400">vs</span>
                  <div className="flex-1 bg-red-500/20 rounded p-2 text-center">
                    <p className="text-red-400 text-xs">Defense</p>
                    <p className="text-white font-bold">{attack_defense_matchup.away_defense_strength?.toFixed(2)}</p>
                  </div>
                </div>
                <p className="text-center mt-2">
                  <span className={`font-semibold ${attack_defense_matchup.home_attack_vs_away_defense > 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {attack_defense_matchup.home_attack_vs_away_defense > 0 ? '+' : ''}{attack_defense_matchup.home_attack_vs_away_defense?.toFixed(2)} Edge
                  </span>
                </p>
              </div>
              
              <div>
                <p className="text-gray-400 text-sm mb-2">Away Attack vs Home Defense</p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-red-500/20 rounded p-2 text-center">
                    <p className="text-red-400 text-xs">Attack</p>
                    <p className="text-white font-bold">{attack_defense_matchup.away_attack_strength?.toFixed(2)}</p>
                  </div>
                  <span className="text-gray-400">vs</span>
                  <div className="flex-1 bg-blue-500/20 rounded p-2 text-center">
                    <p className="text-blue-400 text-xs">Defense</p>
                    <p className="text-white font-bold">{attack_defense_matchup.home_defense_strength?.toFixed(2)}</p>
                  </div>
                </div>
                <p className="text-center mt-2">
                  <span className={`font-semibold ${attack_defense_matchup.away_attack_vs_home_defense > 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {attack_defense_matchup.away_attack_vs_home_defense > 0 ? '+' : ''}{attack_defense_matchup.away_attack_vs_home_defense?.toFixed(2)} Edge
                  </span>
                </p>
              </div>
            </div>
            
            <div className="bg-indigo-500/10 rounded-lg p-4">
              <p className="text-indigo-300 font-semibold mb-2">Matchup Verdict:</p>
              <p className="text-gray-300">{attack_defense_matchup.matchup_verdict}</p>
              <p className="text-gray-400 text-sm mt-2">
                Expected Goals: Home {attack_defense_matchup.expected_goals?.home} - {attack_defense_matchup.expected_goals?.away} Away
              </p>
            </div>
          </Card>

          {/* Key Factors */}
          <Card className="bg-gradient-to-br from-teal-900/30 to-cyan-900/30 border-teal-500/30 p-6">
            <div className="flex items-center gap-2 mb-4">
              <AlertCircle className="w-6 h-6 text-teal-400" />
              <h3 className="text-2xl font-bold text-white">Key Factors</h3>
            </div>
            
            <div className="space-y-3">
              {key_factors.map((factor, index) => (
                <div key={index} className="bg-teal-500/10 rounded-lg p-4 border-l-4 border-teal-500">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-teal-300 font-semibold">{factor.factor}</p>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      factor.impact === 'High' ? 'bg-red-500/30 text-red-300' :
                      factor.impact === 'Medium' ? 'bg-yellow-500/30 text-yellow-300' :
                      'bg-green-500/30 text-green-300'
                    }`}>
                      {factor.impact} Impact
                    </span>
                  </div>
                  <p className="text-gray-300 text-sm">{factor.description}</p>
                </div>
              ))}
            </div>
          </Card>

          {/* Iter 204a — Asian Handicap Value snapshot.  Uses the
              already-computed expected_goals from the matchup card so
              the user gets a one-click AH analysis without leaving the
              modal.  Paste bookmaker lines to score per-line edges. */}
          <AsianHandicapInlineCard
            homeTeam={match_info.home_team}
            awayTeam={match_info.away_team}
            league={match_info.league}
            expectedGoals={attack_defense_matchup?.expected_goals}
            fixtureId={match_info.fixture_id}
          />

          {/* Iter 204b — Goal Timing & Quarter Analysis snapshot. */}
          <GoalTimingInlineCard
            homeTeam={match_info.home_team}
            awayTeam={match_info.away_team}
            league={match_info.league}
            expectedGoals={attack_defense_matchup?.expected_goals}
            fixtureId={match_info.fixture_id}
          />
        </div>
      </div>

      {/* Live 3D tracker modal — sits inside the analysis modal's
          z-50 layer; LiveStreamModal renders its own z-60 backdrop. */}
      {match_info.fixture_id && (
        <LiveStreamModal
          open={showStream}
          onOpenChange={setShowStream}
          fixtureId={match_info.fixture_id}
          homeTeam={match_info.home_team}
          awayTeam={match_info.away_team}
        />
      )}
    </div>
  );
};

export default MatchAnalysisModal;
